// ENG-027: does an answer close the user's question?
//
// The agent backend sends the verdict on its "end" frame (context.closure). The n8n backend
// sends none, so the chat falls back to this TypeScript copy of the gateway rule
// (app/n8n/api-integration/gateway/agent/closure.py). Keep the two in step: the generic-offer
// phrases below must equal GENERIC_OFFER_PHRASES there (checked by
// tests/test_rating_prompt_frontend_contract.py).
//
// Rule, in order (the "closing line" is the last non-empty line of a markdown answer, or the
// conclusion / intro of a structured payload):
//   1. empty, or nothing but leaked tool-call text                 -> error
//   2. structured payload with a "questions" key (discovery form)  -> clarifying
//   3. the closing line asks the user something and is not a generic offer -> clarifying
//      (ends in ? / ; / U+037E, or ":" without product cards; or, without product cards,
//      a request for input such as "Στείλε μου το SKU")
//   4. "nothing found" wording without product cards               -> no_results
//   5. otherwise                                                    -> answered

export type ClosureReason = "answered" | "no_results" | "clarifying" | "error";

export interface AnswerClosure {
    closes: boolean;
    reason: ClosureReason;
}

// Keep in sync with GENERIC_OFFER_PHRASES in gateway/agent/closure.py. Regex fragments, matched
// case-insensitively anywhere in the closing line.
export const GENERIC_OFFER_PHRASES: readonly string[] = [
    "κάτι άλλο",
    "χρειάζεσαι κάτι",
    "θέλεις κάτι",
    "να βοηθήσω σε κάτι",
    "μπορώ να (?:σε |σας )?βοηθήσω",
    "πώς μπορώ",
    "θα ήθελες",
    "αν θέλεις",
    "θέλετε να σας",
    "θ(?:έλ)?ες να (?:(?:σου|σας|το|τα|στο|στα|τον|την|τις|τους) )*[α-ωάέήίόύώϊϋΐΰ]*(?:ω|ώ|ουμε|ούμε)(?![α-ωάέήίόύώϊϋΐΰ])",
    "να (?:ψάξω|ελέγξω|δω) (?:εξωτερικά|και|σε)",
    "να σου (?:βρω|προτείνω|πω|δείξω|ετοιμάσω)",
    "να κάνω αναζήτηση",
    "anything else",
    "want me to",
    "would you like",
    "do you want me",
    "shall i",
    "should i",
    "how can i help"
];

const GENERIC_OFFER_RE = new RegExp(GENERIC_OFFER_PHRASES.map(phrase => `(?:${phrase})`).join("|"), "iu");

const LEAK_RE = /\[\[ ?Getting[^\]]*\]\]|^Calling \S+(?: \S+)? with input:.*$|^\[Used tools:.*$|\{"agent_name"\s*:\s*"[^"]*"\}/gmu;
const MARKER_RE = /\{"type"\s*:\s*"[^"]*"(?:\s*,\s*"message"\s*:\s*"[^"]*")?\s*\}/gu;
const CITATIONS_LINE_RE = /\{"type"\s*:\s*"citations".*$/gmu;
const QUESTIONS_KEY_RE = /"questions"\s*:\s*\[\s*\{/u;
const INTRO_RE = /"intro"\s*:\s*"((?:[^"\\]|\\.)*)"/u;
const REQUEST_RE =
    /^(?:στείλε|στείλ’|πες|γράψε|απάντησέ|απάντησε|διάλεξε|επιβεβαίωσε|δώσε)(?:\s|$)|(?:μόλις|αν|όταν) μου (?:απαντήσεις|στείλεις|δώσεις|πεις|γράψεις)|^(?:reply|tell me|let me know|send me|choose|pick)\b/iu;
const NO_RESULTS_RE =
    /δεν (βρέθηκ|βρήκα|βρίσκω|εντόπισα|εντοπίστηκ|υπάρχ[α-ωάέήίόύώϊϋΐΰ]* (?:διαθέσιμ|αποτέλεσμ|στον κατάλογο))|no (?:results|products|matching)|(?:could not|couldn't|can't|cannot) find/iu;

// Characters that may trail the closing sentence without changing its punctuation.
const TRAILING_DECORATION = new Set(Array.from(" \t*_`\"'»”)]>💡"));
const LEADING_DECORATION = new Set([...TRAILING_DECORATION, "-", "•"]);
const QUESTION_ENDINGS = ["?", ";", "\u037e"];
const OPEN_ENDINGS = [...QUESTION_ENDINGS, ":"];
const RULE_CHARS = new Set(Array.from("-—_*= "));
const LINE_BREAK_RE = /\r\n|[\n\r\v\f\u001c\u001d\u001e\u0085\u2028\u2029]/u;

const stripEnd = (text: string, chars: Set<string>): string => {
    const points = Array.from(text);
    while (points.length && chars.has(points[points.length - 1])) points.pop();
    return points.join("");
};

const stripStart = (text: string, chars: Set<string>): string => {
    const points = Array.from(text);
    let start = 0;
    while (start < points.length && chars.has(points[start])) start++;
    return points.slice(start).join("");
};

// Python truthiness, so the TS copy reads payloads exactly like the gateway does.
const isTruthy = (value: unknown): boolean => {
    if (Array.isArray(value)) return value.length > 0;
    if (value && typeof value === "object") return Object.keys(value).length > 0;
    return Boolean(value);
};

const stripProtocol = (content: string): string => content.replace(LEAK_RE, "").replace(CITATIONS_LINE_RE, "").replace(MARKER_RE, "").trim();

// The first JSON object of an answer that starts with "{", or null.
const structuredPayload = (text: string): Record<string, unknown> | null => {
    if (!text.startsWith("{")) return null;
    let depth = 0;
    let inString = false;
    let escaped = false;
    for (let index = 0; index < text.length; index++) {
        const ch = text[index];
        if (inString) {
            if (escaped) escaped = false;
            else if (ch === "\\") escaped = true;
            else if (ch === '"') inString = false;
            continue;
        }
        if (ch === '"') inString = true;
        else if (ch === "{") depth++;
        else if (ch === "}" && --depth === 0) {
            try {
                const parsed: unknown = JSON.parse(text.slice(0, index + 1));
                return parsed && typeof parsed === "object" && !Array.isArray(parsed) ? (parsed as Record<string, unknown>) : null;
            } catch {
                return null;
            }
        }
    }
    return null;
};

const closingLine = (text: string): string => {
    const lines = text
        .split(LINE_BREAK_RE)
        .map(line => line.trim())
        .filter(Boolean);
    while (lines.length && Array.from(lines[lines.length - 1]).every(ch => RULE_CHARS.has(ch))) lines.pop();
    return lines.length ? lines[lines.length - 1] : "";
};

const asks = (line: string, besideProducts: boolean): boolean => {
    if (GENERIC_OFFER_RE.test(line)) return false;
    const tail = stripEnd(line, TRAILING_DECORATION);
    const endings = besideProducts ? QUESTION_ENDINGS : OPEN_ENDINGS;
    if (endings.some(ending => tail.endsWith(ending))) return true;
    return !besideProducts && REQUEST_RE.test(stripStart(line, LEADING_DECORATION));
};

export function classifyAnswerClosure(content: string | null | undefined): AnswerClosure {
    const text = stripProtocol(content || "");
    if (!text) return { closes: false, reason: "error" };

    let closing: string;
    let hasProducts: boolean;
    const payload = structuredPayload(text);
    if (payload) {
        if (isTruthy(payload.questions)) return { closes: false, reason: "clarifying" };
        const closingValue = isTruthy(payload.conclusion) ? payload.conclusion : isTruthy(payload.intro) ? payload.intro : "";
        closing = String(closingValue).trim();
        hasProducts = isTruthy(payload.products);
    } else if (text.startsWith("{")) {
        // Truncated or otherwise invalid structured payload.
        if (QUESTIONS_KEY_RE.test(text)) return { closes: false, reason: "clarifying" };
        closing = INTRO_RE.exec(text)?.[1] ?? "";
        hasProducts = text.includes('"products"');
    } else {
        closing = closingLine(text);
        hasProducts = false;
    }

    if (closing && asks(closingLine(closing), hasProducts)) return { closes: false, reason: "clarifying" };
    if (!hasProducts && NO_RESULTS_RE.test(text)) return { closes: true, reason: "no_results" };
    return { closes: true, reason: "answered" };
}
